export interface SavedCounterpartyAddress {
    street: string | null;
    street2: string | null;
    city: string | null;
    state: string | null;
    postalCode: string | null;
    country: string | null;
}

const MIN_ADDRESS_KEY_LENGTH = 8;

const ADDRESS_TOKEN_ALIASES: ReadonlyMap<string, string> = new Map([
    ["street", "st"],
    ["str", "st"],
    ["avenue", "ave"],
    ["av", "ave"],
    ["road", "rd"],
    ["boulevard", "blvd"],
    ["drive", "dr"],
    ["lane", "ln"],
    ["court", "ct"],
    ["place", "pl"],
    ["parkway", "pkwy"],
    ["highway", "hwy"],
    ["terrace", "ter"],
    ["circle", "cir"],
    ["square", "sq"],
    ["suite", "ste"],
    ["apartment", "apt"],
    ["building", "bldg"],
    ["floor", "fl"],
    ["room", "rm"],
    ["north", "n"],
    ["south", "s"],
    ["east", "e"],
    ["west", "w"],
    ["northeast", "ne"],
    ["northwest", "nw"],
    ["southeast", "se"],
    ["southwest", "sw"],
]);

const UNIT_DESIGNATORS: ReadonlySet<string> = new Set([
    "ste",
    "apt",
    "unit",
    "bldg",
    "fl",
    "rm",
]);

const TRAILING_COUNTRY_SEQUENCES: ReadonlyArray<ReadonlyArray<string>> = [
    ["united", "states", "of", "america"],
    ["united", "states"],
    ["usa"],
    ["us"],
];

const LEADING_LABEL_SEQUENCES: ReadonlyArray<ReadonlyArray<string>> = [
    ["remit", "to"],
    ["remittance", "address"],
    ["remit"],
];

const FIVE_DIGIT_ZIP = /^\d{5}$/;
const NINE_DIGIT_ZIP = /^\d{9}$/;
const ZIP_PLUS_FOUR_SUFFIX = /^\d{4}$/;

function rawTokens(value: string): string[] {
    return value
        .toLowerCase()
        .replace(/\bp\.?\s*o\.?\s*box\b/g, " pobox ")
        .replace(/\bpost\s+office\s+box\b/g, " pobox ")
        .replace(/#/g, " unit ")
        .split(/[^a-z0-9]+/)
        .filter((token) => token.length > 0);
}

function startsWithSequence(
    tokens: string[],
    sequence: ReadonlyArray<string>,
): boolean {
    return sequence.every((token, index) => tokens[index] === token);
}

function endsWithSequence(
    tokens: string[],
    sequence: ReadonlyArray<string>,
): boolean {
    const offset = tokens.length - sequence.length;
    return (
        offset >= 0 &&
        sequence.every((token, index) => tokens[offset + index] === token)
    );
}

function withoutLeadingLabel(tokens: string[]): string[] {
    const label = LEADING_LABEL_SEQUENCES.find((sequence) =>
        startsWithSequence(tokens, sequence),
    );
    return label ? tokens.slice(label.length) : tokens;
}

function withoutTrailingCountry(tokens: string[]): string[] {
    const country = TRAILING_COUNTRY_SEQUENCES.find(
        (sequence) =>
            tokens.length > sequence.length &&
            endsWithSequence(tokens, sequence),
    );
    return country ? tokens.slice(0, tokens.length - country.length) : tokens;
}

function withFiveDigitZips(tokens: string[]): string[] {
    return tokens.flatMap((token, index) => {
        if (NINE_DIGIT_ZIP.test(token)) {
            return [token.slice(0, 5)];
        }
        const previous = tokens[index - 1];
        const isZipPlusFour =
            index >= 2 &&
            ZIP_PLUS_FOUR_SUFFIX.test(token) &&
            previous != null &&
            FIVE_DIGIT_ZIP.test(previous);
        return isZipPlusFour ? [] : [token];
    });
}

function addressTokens(value: string): string[] {
    const aliased = rawTokens(value).map(
        (token) => ADDRESS_TOKEN_ALIASES.get(token) ?? token,
    );
    return withFiveDigitZips(
        withoutTrailingCountry(withoutLeadingLabel(aliased)),
    );
}

export function addressKey(value: string): string | null {
    const key = addressTokens(value).join(" ");
    return key.replace(/ /g, "").length >= MIN_ADDRESS_KEY_LENGTH ? key : null;
}

export function indexOfTokenSequence(
    tokens: string[],
    sequence: string[],
): number {
    if (sequence.length === 0) {
        return -1;
    }
    for (let start = 0; start + sequence.length <= tokens.length; start++) {
        if (sequence.every((token, index) => tokens[start + index] === token)) {
            return start;
        }
    }
    return -1;
}

function unitValues(tokens: string[]): string[] {
    return tokens.flatMap((token, index) => {
        const next = tokens[index + 1];
        return UNIT_DESIGNATORS.has(token) &&
            next &&
            !UNIT_DESIGNATORS.has(next)
            ? [next]
            : [];
    });
}

function hasConflictingUnit({
    extractedTokens,
    savedStreet2Tokens,
}: {
    extractedTokens: string[];
    savedStreet2Tokens: string[];
}): boolean {
    const savedValues = new Set(
        savedStreet2Tokens.filter((token) => !UNIT_DESIGNATORS.has(token)),
    );
    if (savedValues.size === 0) {
        return false;
    }
    return unitValues(extractedTokens).some((value) => !savedValues.has(value));
}

function hasConflictingZip({
    tokensAfterStreet,
    savedPostalCode,
}: {
    tokensAfterStreet: string[];
    savedPostalCode: string | null;
}): boolean {
    const savedZip = withFiveDigitZips(rawTokens(savedPostalCode ?? "")).find(
        (token) => FIVE_DIGIT_ZIP.test(token),
    );
    if (!savedZip) {
        return false;
    }
    const zips = tokensAfterStreet.filter((token) =>
        FIVE_DIGIT_ZIP.test(token),
    );
    return zips.length > 0 && !zips.includes(savedZip);
}

function hasConflictingCity({
    tokensAfterStreet,
    saved,
}: {
    tokensAfterStreet: string[];
    saved: SavedCounterpartyAddress;
}): boolean {
    const cityTokens = addressTokens(saved.city ?? "");
    if (
        cityTokens.length === 0 ||
        tokensAfterStreet.some((token) => FIVE_DIGIT_ZIP.test(token)) ||
        indexOfTokenSequence(tokensAfterStreet, cityTokens) >= 0
    ) {
        return false;
    }
    const knownTokens = new Set([
        ...UNIT_DESIGNATORS,
        ...unitValues(tokensAfterStreet),
        ...addressTokens(saved.street2 ?? ""),
        ...addressTokens(saved.state ?? ""),
    ]);
    return tokensAfterStreet.some((token) => !knownTokens.has(token));
}

export function presentAddressParts(
    address: SavedCounterpartyAddress,
): string[] {
    return [
        address.street,
        address.street2,
        address.city,
        address.state,
        address.postalCode,
        address.country,
    ].flatMap((part) => {
        const trimmed = part?.trim();
        return trimmed ? [trimmed] : [];
    });
}

export function isSavedAddress({
    extracted,
    saved,
}: {
    extracted: string;
    saved: SavedCounterpartyAddress;
}): boolean {
    const extractedTokens = addressTokens(extracted);
    if (extractedTokens.length === 0) {
        return false;
    }
    const savedTokens = addressTokens(presentAddressParts(saved).join(" "));
    if (indexOfTokenSequence(savedTokens, extractedTokens) >= 0) {
        return true;
    }

    const streetTokens = addressTokens(saved.street ?? "");
    const streetIndex = indexOfTokenSequence(extractedTokens, streetTokens);
    if (streetIndex < 0) {
        return false;
    }
    const tokensAfterStreet = extractedTokens.slice(
        streetIndex + streetTokens.length,
    );
    return (
        !hasConflictingUnit({
            extractedTokens,
            savedStreet2Tokens: addressTokens(saved.street2 ?? ""),
        }) &&
        !hasConflictingZip({
            tokensAfterStreet,
            savedPostalCode: saved.postalCode,
        }) &&
        !hasConflictingCity({ tokensAfterStreet, saved })
    );
}
